import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUser,
  FaWrench,
  FaBoxOpen,
  FaCar,
  FaEdit,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { fetchAllCompanyUsers } from '../../WorkForce/utils/workforcePeople';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import AllocationDetailModal from './AllocationDetailModal';
import '../styles/InventoryEquipment.css';
import '../styles/InventoryAllocation.css';

const LABOUR_RATE = 30;
const TASK_HOURS = 8;

const PROJECTS = `
  query AllocationProjects {
    findAllProject(limit: 200) {
      id
      projectId
      name
      status
      manager { id firstName lastName }
      booking { id fullName address }
    }
  }
`;

const TASKS = `
  query AllocationTasks {
    getAdminRosterTasks {
      id
      title
      status
      user { id firstName lastName }
      project { id name projectId }
    }
  }
`;

const INVENTORY = `
  query AllocationInventory($limit: Int) {
    findAllInventoryManagement(limit: $limit) {
      id
      quantityAssigned
      category
      updateAction
      assignedBy
      status
      tool { id name code category perItemCost unit }
      project {
        id
        name
        projectId
        status
      }
    }
  }
`;

const USERS = `
  query AllocationUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        jobTitle
        roles
      }
      totalPages
    }
  }
`;

async function loadSource(label, fn) {
  try {
    return await fn();
  } catch (err) {
    toast.error(`${label}: ${err?.message || 'Failed to load'}`);
    return {};
  }
}

const EQUIPMENT = `
  query AllocationEquipment {
    findAllEquipment {
      id
      assetNumber
      equipmentType
      brandModel
      assignedTo
      currentSite
    }
  }
`;

const VEHICLES = `
  query AllocationVehicles {
    findAllVehicles {
      id
      vehicleNumber
      makeModel
      assignedDriver
      currentSite
    }
  }
`;

const TABS = [
  { id: 'site', label: 'By Site' },
  { id: 'employee', label: 'By Employee' },
  { id: 'job', label: 'By Job' },
];
const TAB_IDS = TABS.map((t) => t.id);
const ALLOCATION_VIEW_KEYS = [{ param: 'job', rowKey: 'jobCode' }];
const ALLOCATION_CLEAR_KEYS = ['job'];

function personName(p) {
  return `${p?.firstName || ''} ${p?.lastName || ''}`.trim();
}

function initials(p) {
  const a = String(p?.firstName || '').charAt(0);
  const b = String(p?.lastName || '').charAt(0);
  return (`${a}${b}` || '?').toUpperCase();
}

function itemKind(rec) {
  const cat = String(rec?.category || rec?.tool?.category || '').toLowerCase();
  const hay = `${rec?.tool?.name || ''} ${rec?.tool?.code || ''} ${rec?.updateAction || ''}`.toLowerCase();
  if (cat.includes('vehicle') || hay.includes('veh-') || /van|vehicle/.test(hay)) {
    return 'vehicle';
  }
  if (
    cat.includes('equipment') ||
    cat.includes('tool') ||
    /vacuum|scrubber|extractor|washer|ass-/.test(hay)
  ) {
    return 'equipment';
  }
  return 'inventory';
}

function siteKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase();
}

function unique(list) {
  return [...new Set(list.filter(Boolean))];
}

function resourceItem({ qty = 0, name, code, assignedTo } = {}) {
  return {
    qty: Number(qty) || 0,
    name: String(name || 'Item').trim() || 'Item',
    code: String(code || '').replace(/^#/, '').trim(),
    assignedTo: String(assignedTo || '').trim(),
  };
}

function resourceKey(item) {
  if (typeof item === 'string') return item.toLowerCase();
  return [item.qty, item.name, item.code, item.assignedTo]
    .join('|')
    .toLowerCase();
}

function uniqueItems(list) {
  const seen = new Set();
  return (list || []).filter((item) => {
    const key = resourceKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function fromMove(rec) {
  return resourceItem({
    qty: rec?.quantityAssigned,
    name: rec?.tool?.name,
    code: rec?.tool?.code,
    assignedTo: rec?.assignedBy,
  });
}

function CountStat({ value, label }) {
  return (
    <div className="ial-stat">
      <em>{value}</em>
      <span>{label}</span>
    </div>
  );
}

function NamePills({ names = [], limit }) {
  if (!names.length) return <p className="ial-none">None assigned</p>;
  const shown = limit == null ? names : names.slice(0, limit);
  const extra = names.length - shown.length;
  return (
    <div className="ial-pills">
      {shown.map((name) => (
        <span key={name} className="ial-pill">
          {name}
        </span>
      ))}
      {extra > 0 ? (
        <span className="ial-pill ial-pill--more">+{extra}</span>
      ) : null}
    </div>
  );
}

function ResourcePreview({ items = [], limit }) {
  if (!items.length) return <p className="ial-none">None assigned</p>;
  const shown = limit == null ? items : items.slice(0, limit);
  const extra = items.length - shown.length;
  return (
    <ul className="ial-items">
      {shown.map((item) => {
        const isText = typeof item === 'string';
        const title = isText ? item : item.name;
        const qty = isText ? 0 : item.qty;
        const code = isText ? '' : item.code;
        const who = isText ? '' : item.assignedTo;
        const meta = [code ? `#${code}` : '', who].filter(Boolean).join(' · ');
        return (
          <li key={resourceKey(item)} className="ial-item">
            {qty ? <span className="ial-qty">{qty}</span> : null}
            <div className="ial-item-text">
              <span className="ial-item-name">{title}</span>
              {meta ? <span className="ial-item-meta">{meta}</span> : null}
            </div>
          </li>
        );
      })}
      {extra > 0 ? (
        <li className="ial-items-more">{extra} more</li>
      ) : null}
    </ul>
  );
}

function CardSection({ icon, label, count, children }) {
  if (!count) return null;
  return (
    <section className="ial-section">
      <div className="ial-section-label">
        {icon}
        <span>{label}</span>
        <em>{count}</em>
      </div>
      {children}
    </section>
  );
}

function isOutgoingAssignment(rec) {
  if (!rec?.project?.id) return false;
  const action = String(rec.updateAction || '').toLowerCase();
  if (action.includes('add to stock')) return false;
  if (/\breturn\b/.test(action)) return false;
  return true;
}

function siteAliases(project) {
  return unique([
    project?.name,
    project?.projectId,
    project?.booking?.fullName,
    project?.booking?.address,
  ]).map(siteKey);
}

function matchesSite(value, aliases) {
  const key = siteKey(value);
  return Boolean(key) && aliases.includes(key);
}

function emptySiteRow(id, site, extra = {}) {
  return {
    id,
    site,
    jobCode: extra.jobCode || '',
    status: extra.status || 'Active',
    staff: [],
    equipment: [],
    inventory: [],
    vehicles: [],
    tasks: [],
    aliases: extra.aliases || [siteKey(site)],
    chemicalCost: 0,
  };
}

function jobCode(project, index) {
  const raw = String(project?.projectId || '').trim();
  if (raw) return raw.startsWith('JOB') ? raw : raw;
  return `JOB-${String(index + 1).padStart(4, '0')}`;
}

function jobInitials(code) {
  const m = String(code || '').match(/(\d+)/);
  if (m) return m[1].slice(-2);
  const letters = String(code || '').replace(/[^A-Za-z]/g, '');
  return (letters.slice(0, 2) || 'JB').toUpperCase();
}

function statusLabel(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'Active';
  if (s === 'complete') return 'Complete';
  if (s === 'inactive') return 'Inactive';
  return 'Active';
}

function detailFromRow(row) {
  if (!row) return null;
  if (row.site != null || row.jobCode != null) return row;
  return {
    site: personName(row),
    jobCode: row.jobTitle,
    status: 'Assigned',
    staff: [personName(row)],
    equipment: row.resources || [],
    inventory: [],
    vehicles: (row.resources || []).filter((r) =>
      /VEH-|Van/i.test(typeof r === 'string' ? r : r?.code || r?.name || ''),
    ),
    tasks: row.tasks || [],
    labourHours: row.hours,
    labourCost: (row.hours || 0) * LABOUR_RATE,
    chemicalCost: 0,
    totalCost: (row.hours || 0) * LABOUR_RATE,
  };
}

export default function AllocationDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [moves, setMoves] = useState([]);
  const [users, setUsers] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, taskRes, invRes, userList, eqRes, vehRes] =
        await Promise.all([
          loadSource('Projects', () => graphqlRequest(PROJECTS, { limit: 200 })),
          loadSource('Roster tasks', () => graphqlRequest(TASKS)),
          loadSource('Inventory', () =>
            graphqlRequest(INVENTORY, { limit: 2000 }),
          ),
          fetchAllCompanyUsers(graphqlRequest, USERS).catch((err) => {
            toast.error(`People: ${err?.message || 'Failed to load'}`);
            return [];
          }),
          loadSource('Equipment', () => graphqlRequest(EQUIPMENT)),
          loadSource('Vehicles', () => graphqlRequest(VEHICLES)),
        ]);
      setProjects(
        Array.isArray(projRes?.findAllProject) ? projRes.findAllProject : [],
      );
      setTasks(
        Array.isArray(taskRes?.getAdminRosterTasks)
          ? taskRes.getAdminRosterTasks
          : [],
      );
      setMoves(
        Array.isArray(invRes?.findAllInventoryManagement)
          ? invRes.findAllInventoryManagement
          : [],
      );
      setUsers(Array.isArray(userList) ? userList : []);
      setEquipment(
        Array.isArray(eqRes?.findAllEquipment) ? eqRes.findAllEquipment : [],
      );
      setVehicles(
        Array.isArray(vehRes?.findAllVehicles) ? vehRes.findAllVehicles : [],
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to load allocations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const goInventory = () => {
    navigate({ pathname: '/', search: '?page=inventory-update' }, { replace: true });
  };

  const siteRows = useMemo(() => {
    const byId = new Map();

    const ensureProject = (project) => {
      if (!project?.id) return null;
      if (!byId.has(project.id)) {
        byId.set(
          project.id,
          emptySiteRow(
            project.id,
            project.name || project.booking?.fullName || 'Site',
            {
              jobCode: jobCode(project, byId.size),
              status: statusLabel(project.status),
              aliases: siteAliases(project),
            },
          ),
        );
      }
      return byId.get(project.id);
    };

    (moves || []).filter(isOutgoingAssignment).forEach((rec) => {
      const project =
        (projects || []).find((p) => p.id === rec.project.id) || rec.project;
      const row = ensureProject(project);
      if (!row) return;
      const kind = itemKind(rec);
      const item = fromMove(rec);
      if (kind === 'equipment') row.equipment.push(item);
      else if (kind === 'vehicle') row.vehicles.push(item);
      else row.inventory.push(item);
      if (rec.assignedBy) row.staff.push(rec.assignedBy);
      if (kind === 'inventory') {
        row.chemicalCost +=
          (Number(rec.quantityAssigned) || 0) *
          (Number(rec.tool?.perItemCost) || 0);
      }
    });

    (equipment || []).forEach((eq) => {
      if (!eq.currentSite && !eq.assignedTo) return;
      const item = resourceItem({
        qty: 1,
        name: eq.equipmentType || eq.brandModel || 'Equipment',
        code: eq.assetNumber,
        assignedTo: eq.assignedTo,
      });
      const project = (projects || []).find((p) =>
        matchesSite(eq.currentSite, siteAliases(p)),
      );
      if (project) {
        const row = ensureProject(project);
        row.equipment.push(item);
        if (eq.assignedTo) row.staff.push(eq.assignedTo);
        return;
      }
      if (!eq.currentSite) return;
      const id = `site:${siteKey(eq.currentSite)}`;
      if (!byId.has(id)) byId.set(id, emptySiteRow(id, eq.currentSite));
      const row = byId.get(id);
      row.equipment.push(item);
      if (eq.assignedTo) row.staff.push(eq.assignedTo);
    });

    (vehicles || []).forEach((vehicle) => {
      if (!vehicle.currentSite && !vehicle.assignedDriver) return;
      const item = resourceItem({
        qty: 1,
        name: vehicle.makeModel || 'Vehicle',
        code: vehicle.vehicleNumber,
        assignedTo: vehicle.assignedDriver,
      });
      const project = (projects || []).find((p) =>
        matchesSite(vehicle.currentSite, siteAliases(p)),
      );
      if (project) {
        const row = ensureProject(project);
        row.vehicles.push(item);
        if (vehicle.assignedDriver) row.staff.push(vehicle.assignedDriver);
        return;
      }
      if (!vehicle.currentSite) return;
      const id = `site:${siteKey(vehicle.currentSite)}`;
      if (!byId.has(id)) byId.set(id, emptySiteRow(id, vehicle.currentSite));
      const row = byId.get(id);
      row.vehicles.push(item);
      if (vehicle.assignedDriver) row.staff.push(vehicle.assignedDriver);
    });

    (tasks || []).forEach((task) => {
      if (!task.project?.id || !byId.has(task.project.id)) return;
      const row = byId.get(task.project.id);
      if (task.title) row.tasks.push(task.title);
      if (task.user) row.staff.push(personName(task.user));
    });

    return [...byId.values()]
      .map((row) => {
        const staff = unique(row.staff);
        const hours =
          Math.max(unique(row.tasks).length, staff.length, 1) * TASK_HOURS;
        const labourCost = hours * LABOUR_RATE;
        return {
          ...row,
          staff,
          equipment: uniqueItems(row.equipment),
          inventory: uniqueItems(row.inventory),
          vehicles: uniqueItems(row.vehicles),
          tasks: unique(row.tasks),
          labourHours: hours,
          labourCost,
          chemicalCost: row.chemicalCost || 0,
          totalCost: labourCost + (row.chemicalCost || 0),
        };
      })
      .filter(
        (row) =>
          row.equipment.length ||
          row.inventory.length ||
          row.vehicles.length,
      );
  }, [projects, tasks, moves, equipment, vehicles]);

  const employeeRows = useMemo(() => {
    const byId = new Map();
    users.forEach((u) => {
      byId.set(u.id, {
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        jobTitle: u.jobTitle || (Array.isArray(u.roles) ? u.roles[0] : '') || 'Cleaner',
        sites: [],
        tasks: [],
        resources: [],
        hours: 0,
      });
    });
    tasks.forEach((t) => {
      if (!t.user?.id) return;
      if (!byId.has(t.user.id)) {
        byId.set(t.user.id, {
          id: t.user.id,
          firstName: t.user.firstName,
          lastName: t.user.lastName,
          jobTitle: t.user.jobTitle || 'Cleaner',
          sites: [],
          tasks: [],
          resources: [],
          hours: 0,
        });
      }
      const row = byId.get(t.user.id);
      if (t.project?.name) row.sites.push(t.project.name);
      if (t.title) row.tasks.push(t.title);
      row.hours += TASK_HOURS;
    });
    equipment.forEach((eq) => {
      const name = String(eq.assignedTo || '').trim().toLowerCase();
      if (!name) return;
      const row = [...byId.values()].find(
        (u) => personName(u).toLowerCase() === name,
      );
      if (row) {
        row.resources.push(`${eq.equipmentType || eq.brandModel} #${eq.assetNumber}`);
        if (eq.currentSite) row.sites.push(eq.currentSite);
      }
    });
    vehicles.forEach((v) => {
      const name = String(v.assignedDriver || '').trim().toLowerCase();
      if (!name) return;
      const row = [...byId.values()].find(
        (u) => personName(u).toLowerCase() === name,
      );
      if (row) {
        row.resources.push(`${v.makeModel || 'Vehicle'} #${v.vehicleNumber}`);
        if (v.currentSite) row.sites.push(v.currentSite);
      }
    });

    return [...byId.values()]
      .map((row) => ({
        ...row,
        sites: unique(row.sites),
        tasks: unique(row.tasks),
        resources: unique(row.resources),
        hours: row.hours || (row.sites.length ? TASK_HOURS : 0),
      }))
      .filter((row) => row.sites.length || row.tasks.length || row.resources.length)
      .sort((a, b) => personName(a).localeCompare(personName(b)));
  }, [users, tasks, equipment, vehicles]);

  const overlayRows = useMemo(
    () => [...siteRows, ...employeeRows],
    [siteRows, employeeRows],
  );
  const overlay = useAdminListOverlay(overlayRows, {
    allowedTabs: TAB_IDS,
    extraViewKeys: ALLOCATION_VIEW_KEYS,
    extraClearKeys: ALLOCATION_CLEAR_KEYS,
  });
  const tab = overlay.tab;
  const detail = detailFromRow(overlay.viewing);

  return (
    <div className="ial-page">
      <header className="ial-header">
        <h1>Resource Allocation</h1>
        <div className="ial-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`ial-tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => overlay.setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="ial-empty">Loading allocations…</div>
      ) : tab === 'site' ? (
        siteRows.length === 0 ? (
          <div className="ial-empty">
            No resources assigned yet. Issue items from Inventory Updates to a
            project, or set a current site on Equipment / Vehicles.
          </div>
        ) : (
          <div className="ial-cards">
            {siteRows.map((row) => (
              <article key={row.id} className="ial-card">
                <div className="ial-card-head">
                  <div>
                    <h3>{row.site}</h3>
                    {row.jobCode ? (
                      <p className="ial-card-job">{row.jobCode}</p>
                    ) : null}
                  </div>
                  <span className="ieq-pill ieq-pill--active">{row.status}</span>
                </div>
                <div className="ial-stats">
                  <CountStat value={row.staff.length} label="Staff" />
                  <CountStat value={row.equipment.length} label="Equipment" />
                  <CountStat value={row.inventory.length} label="Inventory" />
                  <CountStat value={row.vehicles.length} label="Vehicles" />
                </div>
                <div className="ial-card-body">
                  <CardSection
                    icon={<FaUser />}
                    label="Staff"
                    count={row.staff.length}
                  >
                    <NamePills names={row.staff} />
                  </CardSection>
                  <CardSection
                    icon={<FaWrench />}
                    label="Equipment"
                    count={row.equipment.length}
                  >
                    <ResourcePreview items={row.equipment} />
                  </CardSection>
                  <CardSection
                    icon={<FaBoxOpen />}
                    label="Inventory"
                    count={row.inventory.length}
                  >
                    <ResourcePreview items={row.inventory} />
                  </CardSection>
                  <CardSection
                    icon={<FaCar />}
                    label="Vehicles"
                    count={row.vehicles.length}
                  >
                    <ResourcePreview items={row.vehicles} />
                  </CardSection>
                </div>
                <div className="ial-card-actions">
                  <button
                    type="button"
                    className="ieq-btn ieq-btn--primary"
                    onClick={() => overlay.openView(row)}
                  >
                    View Allocation
                  </button>
                  <button
                    type="button"
                    className="ieq-icon-btn"
                    title="Assign from Inventory Update"
                    onClick={goInventory}
                  >
                    <FaEdit />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )
      ) : tab === 'employee' ? (
        employeeRows.length === 0 ? (
          <div className="ial-empty">No staff allocations yet. Assign tasks or equipment to employees.</div>
        ) : (
          <div className="ial-emp-list">
            {employeeRows.map((row) => (
              <article key={row.id} className="ial-emp">
                <div className="ial-avatar">{initials(row)}</div>
                <div>
                  <div className="ial-emp-name">{personName(row)}</div>
                  <div className="ial-emp-role">{row.jobTitle}</div>
                </div>
                <div>
                  <div className="ial-muted">Sites</div>
                  <NamePills names={row.sites} limit={2} />
                </div>
                <div className="ial-hours">{row.hours} hrs</div>
                <div>
                  <div className="ial-muted">Resources</div>
                  <ResourcePreview items={row.resources} limit={2} />
                </div>
                <div>
                  <div className="ial-muted">Task</div>
                  <div>{row.tasks[0] || '—'}</div>
                  {row.tasks.length > 1 ? (
                    <div className="ial-muted">+{row.tasks.length - 1} more</div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="ieq-btn"
                  onClick={() => overlay.openView(row)}
                >
                  View
                </button>
              </article>
            ))}
          </div>
        )
      ) : siteRows.length === 0 ? (
        <div className="ial-empty">
          No jobs with allocated inventory, equipment, or vehicles.
        </div>
      ) : (
        <div className="ial-emp-list">
          {siteRows.map((row) => {
            const resources = uniqueItems([
              ...row.equipment,
              ...row.inventory,
              ...row.vehicles,
            ]);
            return (
              <article key={row.id} className="ial-emp">
                <div className="ial-avatar">{jobInitials(row.jobCode)}</div>
                <div>
                  <div className="ial-emp-name">{row.jobCode}</div>
                  <div className="ial-emp-role">{row.status}</div>
                </div>
                <div>
                  <div className="ial-muted">Site</div>
                  <div>{row.site || '—'}</div>
                </div>
                <div className="ial-hours">{row.labourHours} hrs</div>
                <div>
                  <div className="ial-muted">Resources</div>
                  <ResourcePreview items={resources} limit={2} />
                </div>
                <div>
                  <div className="ial-muted">Task</div>
                  <div>{row.tasks[0] || 'Add task'}</div>
                  {row.tasks.length > 1 ? (
                    <div className="ial-muted">+{row.tasks.length - 1} more</div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="ieq-btn"
                  onClick={() => overlay.openView(row)}
                >
                  View
                </button>
              </article>
            );
          })}
        </div>
      )}

      {detail && (
        <AllocationDetailModal
          title={detail.site}
          subtitle={detail.jobCode}
          statusLabel={detail.status}
          staff={detail.staff}
          equipment={detail.equipment}
          inventory={detail.inventory}
          vehicles={detail.vehicles}
          tasks={detail.tasks}
          labourHours={detail.labourHours}
          labourCost={detail.labourCost}
          chemicalCost={detail.chemicalCost}
          totalCost={detail.totalCost}
          onClose={overlay.closeOverlay}
          onEdit={goInventory}
        />
      )}
    </div>
  );
}
